import type { Component, Page, PageSnapshot, Project } from "@/types";
import type { PersistedProjectState } from "@/services/api/projects";
import { repairPagesWithComponentsBindingScopes } from "./bindingOrgScopeRepair";

export interface PageWithComponentsForPersistence {
  pageId: string;
  page: Page;
  components: Component[];
}

export interface BuiltPersistedProjectState extends PersistedProjectState {
  pages: PageSnapshot[];
  components: Component[];
  isMultiPage: true;
}

interface BuildPersistedProjectStateOptions {
  pagesWithComponents: PageWithComponentsForPersistence[];
  currentPageId?: string;
  schemaVersion?: Project["schemaVersion"];
  lastModified?: Date | string;
  snapshotName?: string;
  settings?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export function cloneComponentForPersistence(component: Component): Component {
  const persistedComponent: Component = {
    ...component,
    properties: { ...component.properties },
    position: component.position ? { ...component.position } : undefined,
    size: { ...component.size },
    style: { ...component.style },
    responsivePosition: { ...component.responsivePosition },
    children: [...component.children],
    metadata: component.metadata ? { ...component.metadata } : undefined,
  };

  delete persistedComponent._childComponents;

  return persistedComponent;
}

export function buildPersistedPageSnapshots(
  pagesWithComponents: PageWithComponentsForPersistence[],
): PageSnapshot[] {
  return pagesWithComponents.map(({ page, pageId, components }) => {
    const persistedComponents = components
      .filter((component) => !component.isSubComponent)
      .map(cloneComponentForPersistence);

    return {
      pageId: pageId || page.id,
      pageName: page.name,
      components: persistedComponents,
      canvasSettings: page.canvasSettings,
      navigation: page.navigation,
      metadata: {
        ...page.metadata,
        componentCount: persistedComponents.length,
      },
    };
  });
}

export async function buildPersistedPageSnapshotsWithBindingScopeRepair(
  pagesWithComponents: PageWithComponentsForPersistence[],
): Promise<PageSnapshot[]> {
  const repairedPagesWithComponents = await repairPagesWithComponentsBindingScopes(
    pagesWithComponents,
  );

  return buildPersistedPageSnapshots(repairedPagesWithComponents);
}

export function getPageSnapshotComponents(
  pages: PageSnapshot[],
  pageId?: string,
): Component[] {
  if (!pageId) {
    return [];
  }

  return pages.find((page) => page.pageId === pageId)?.components || [];
}

export function buildPersistedProjectState({
  pagesWithComponents,
  currentPageId,
  schemaVersion,
  lastModified,
  snapshotName,
  settings,
  metadata,
}: BuildPersistedProjectStateOptions): BuiltPersistedProjectState {
  const pages = buildPersistedPageSnapshots(pagesWithComponents);
  const components = getPageSnapshotComponents(pages, currentPageId);

  return {
    pages,
    components,
    isMultiPage: true,
    ...(schemaVersion ? { schemaVersion } : {}),
    ...(lastModified ? { lastModified } : {}),
    ...(snapshotName ? { snapshotName } : {}),
    ...(settings ? { settings } : {}),
    ...(metadata ? { metadata } : {}),
  };
}

export async function buildPersistedProjectStateWithBindingScopeRepair({
  pagesWithComponents,
  currentPageId,
  schemaVersion,
  lastModified,
  snapshotName,
  settings,
  metadata,
}: BuildPersistedProjectStateOptions): Promise<BuiltPersistedProjectState> {
  const repairedPagesWithComponents = await repairPagesWithComponentsBindingScopes(
    pagesWithComponents,
  );

  return buildPersistedProjectState({
    pagesWithComponents: repairedPagesWithComponents,
    currentPageId,
    schemaVersion,
    lastModified,
    snapshotName,
    settings,
    metadata,
  });
}
